import Cookies from 'js-cookie';

export type ConsentValue = 'granted' | 'denied';

export type ConsentAction = 'default' | 'update';

export type ConsentSettings = {
  ad_storage: ConsentValue;
  ad_user_data: ConsentValue;
  ad_personalization: ConsentValue;
  analytics_storage: ConsentValue;
};

export type ConsentPreferences = {
  analyticsAllowed: boolean;
  marketingAllowed: boolean;
};

export const CONSENT_COOKIE_NAME = '_c';

const CONSENT_COOKIE_OPTIONS = {
  expires: 365,
  path: '/',
} as const;

const createDefaultConsentSettings = (): ConsentSettings => ({
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
});

const ensureGtag = (): void => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const windowAny = window as any;

  if (windowAny.gtag) {
    return;
  }

  windowAny.dataLayer = windowAny.dataLayer || [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  windowAny.gtag = function gtag(...args: any[]) {
    windowAny.dataLayer.push(args);
  };
};

export const updateGTMConsent = (consent: ConsentSettings): void => {
  ensureGtag();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const windowAny = window as any;
  windowAny.gtag('consent', 'update', consent);

  windowAny.dataLayer.push({
    event: 'gtm_consent_update',
    ...consent,
  });
};

export const setDefaultGTMConsent = (): void => {
  ensureGtag();

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const windowAny = window as any;
  windowAny.gtag('consent', 'default', createDefaultConsentSettings());
};

export const getConsentValue = (allowed: boolean): ConsentValue => (allowed ? 'granted' : 'denied');

export const parseConsentCookie = (cookieValue: string): ConsentSettings => {
  const [, analytical, marketing] = cookieValue.split(';').map((value) => value === '1');

  return {
    ad_storage: getConsentValue(marketing),
    ad_user_data: getConsentValue(marketing),
    ad_personalization: getConsentValue(marketing),
    analytics_storage: getConsentValue(analytical),
  };
};

export const getConsentCookieValue = (): string | undefined => Cookies.get(CONSENT_COOKIE_NAME);

export const isAnalyticalAllowed = (): boolean => {
  const cookieValue = getConsentCookieValue();
  return cookieValue ? cookieValue.split(';')[1] === '1' : false;
};

export const setConsentCookieValue = (cookieValue: string): void => {
  Cookies.set(CONSENT_COOKIE_NAME, cookieValue, CONSENT_COOKIE_OPTIONS);
};

export const buildConsentCookieValue = ({ analyticsAllowed, marketingAllowed }: ConsentPreferences): string =>
  `1;${analyticsAllowed ? '1' : '0'};${marketingAllowed ? '1' : '0'}`;

export const applyConsentPreferences = (preferences: ConsentPreferences): void => {
  const cookieValue = buildConsentCookieValue(preferences);
  setConsentCookieValue(cookieValue);
  updateGTMConsent(parseConsentCookie(cookieValue));
};

/**
 * Cookie format: '1;0;0' = functional;analytical;marketing
 */
export const updateConsentFromCookie = (): void => {
  const cookieValue = getConsentCookieValue();

  if (!cookieValue) {
    return;
  }

  updateGTMConsent(parseConsentCookie(cookieValue));
};

let gtmScriptLoaded = false;

/**
 * Idempotently inject the GTM container script.
 * Should only be called after the user has made a consent choice.
 */
export const loadGTMScript = (containerId = 'GTM-T7T88D8'): void => {
  if (gtmScriptLoaded) return;
  gtmScriptLoaded = true;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const windowAny = window as any;
  windowAny.dataLayer = windowAny.dataLayer || [];
  windowAny.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
  document.head.appendChild(script);
};

export default updateConsentFromCookie;
